import { produce } from "immer"
import { isEmpty, forEach, mapValues, pickBy, isObject } from "lodash"
import { POSITIONS_SUBQUERY } from "hooks/rebalancer"
import { IsoDate } from "helpers/types/dates"
import { ASSET_TYPES, ASSET_TYPES_MAP, SINGULAR_ASSET_TYPES } from "helpers/assets/constants"
import { buildContributorKey } from "helpers/contributors"
import { camelToUpperSnake } from "helpers/string"

export const PORTFOLIO_QUERY =
  `portfolio (id: $portfolioId) {
      dates(from: $date, to: $date) {
        ${POSITIONS_SUBQUERY}
      }
    }
  `

type Positions = Record<string, Position>

interface ContributorId {
  assetClass: string
  modelProviderId: string
  internalId: string
  id: string
  identifier: string
  isin?: string
  ticker?: string
  mic?: string
  figi?: string
  bloombergTicker?: string
}

interface Attribute {
  category: string
  value: string
}

type AttributesKey = `attributes${string}`

interface AttributeContributor {
  id: string | Partial<ContributorId>
  economicExposure: number
  attributes?: Attribute[] | null
}

type TransformedAttribute = Record<string, number>

type TransformedAttributeRecord = Record<string, TransformedAttribute>

interface PortfolioDate {
  commodities: AttributeContributor[] | null
  currencies: AttributeContributor[] | null
  date: IsoDate
  equities: AttributeContributor[] | null
  equity: number | null
  fixedIncome: AttributeContributor[] | null
  indexes: AttributeContributor[] | null
  otherAssets: AttributeContributor[] | null
  swaps: AttributeContributor[] | null
}

interface Portfolio {
  dates: PortfolioDate[]
}

interface CompositionValues {
  identifier?: string
  mic?: string
  internalId?: string
}

type PositionValues = Record<string, CompositionValues | Record<string, SortedAttributeValues>>

interface Position {
  values: PositionValues
  label: string
  children?: Record<string, Position>
}

interface SortedAttributeValues {
  items: string[]
  exposures: number[]
}

function getContributorKey(
  contributorId: string | Partial<ContributorId>,
  assetType?: keyof typeof SINGULAR_ASSET_TYPES,
): string {
  let contributorIds: Partial<ContributorId>

  if (typeof contributorId === "string") {
    const [identifier] = contributorId.split(".")
    contributorIds = { id: contributorId, identifier }
  } else {
    const equityCompositeId = contributorId.ticker && contributorId.mic
      ? `${contributorId.ticker}.${contributorId.mic}`
      : undefined

    const normalizedId =
      contributorId.id
  || (!contributorId.modelProviderId ? equityCompositeId : undefined)

    contributorIds = {
      ...contributorId,
      id: normalizedId,
      identifier:
    contributorId.identifier
    || normalizedId
    || contributorId.isin
    || contributorId.figi
    || contributorId.bloombergTicker
    || contributorId.internalId,
    }
  }

  if (!assetType) {
    return buildContributorKey(contributorIds as ContributorId)
  }

  const singularType = SINGULAR_ASSET_TYPES[assetType]
  const assetClass = singularType
    ? ASSET_TYPES_MAP[camelToUpperSnake(singularType) as keyof typeof ASSET_TYPES_MAP]
    : undefined

  return buildContributorKey({ ...contributorIds, assetClass: assetClass ?? contributorIds.assetClass } as ContributorId)
}

function accumulateAttributeExposureInPlace(
  currentItems: TransformedAttributeRecord | null,
  newItems: Attribute[] | null,
  economicExposure: number,
): TransformedAttributeRecord {
  const items = currentItems || {}

  if (!newItems) {
    return items
  }

  for (const { category, value } of newItems) {
    const lowerCategory = category.toLowerCase()

    if (!items[lowerCategory]) {
      items[lowerCategory] = {}
    }

    items[lowerCategory][value] = (items[lowerCategory][value] ?? 0) + economicExposure
  }

  return items
}

function sortByAbsoluteComposition(attribute: Record<string, number>): SortedAttributeValues {
  const attributeValues = Object.entries(attribute)
  attributeValues.sort(([, value1], [, value2]) => Math.abs(value2) - Math.abs(value1))

  return attributeValues.reduce((acc, [key, value]) => {
    acc.items.push(key)
    acc.exposures.push(value)
    return acc
  }, { items: [], exposures: [] } as SortedAttributeValues)
}

function processContributorAttributes(
  contributor: AttributeContributor,
  contributorKey: string,
  attributesMap: Record<string, TransformedAttributeRecord>,
): void {
  if (!contributor.attributes) {
    return
  }

  const current = attributesMap[contributorKey]
  attributesMap[contributorKey] = accumulateAttributeExposureInPlace(
    current,
    contributor.attributes,
    contributor.economicExposure,
  )
}

function extractAttributes(date: PortfolioDate): Record<string, TransformedAttributeRecord> {
  const attributesMap: Record<string, TransformedAttributeRecord> = {}

  ASSET_TYPES.forEach(assetType => {
    const contributors = date[assetType] ?? []

    contributors.forEach(contributor => {
      const contributorKey = getContributorKey(contributor.id, assetType)
      processContributorAttributes(contributor, contributorKey, attributesMap)
    })
  })

  return attributesMap
}

function applyAttributesToPositionInPlace(
  position: Position,
  attributesField: AttributesKey,
  attributeValues: TransformedAttributeRecord | undefined,
  groupById?: string,
  parentLabel?: string,
): void {
  if (!attributeValues) {
    position.values[attributesField] = {}
    return
  }
  const currentAttributeExposure =
    groupById && parentLabel ? attributeValues?.[groupById]?.[parentLabel] : undefined

  let filteredAttributeValues = attributeValues
  if (currentAttributeExposure != null) {
    filteredAttributeValues = Object.entries(attributeValues).reduce((acc, [key, values]) => {
      const filteredValues = pickBy((values ?? {}), val => val === currentAttributeExposure)
      if (isObject(filteredValues)) {
        acc[key] = filteredValues
      }
      return acc
    }, {} as TransformedAttributeRecord)
  }
  const mappedValues = mapValues(filteredAttributeValues, sortByAbsoluteComposition)
  position.values[attributesField] = mappedValues
}

function mapAttributesToChildren(
  position: Position,
  positionKey: string,
  attributes: Record<string, TransformedAttributeRecord>,
  keySuffix: string,
  groupById?: string,
  parentLabel?: string,
): void {
  const attributesField: AttributesKey = `attributes${keySuffix}`
  const attributeValues = attributes[positionKey]
  applyAttributesToPositionInPlace(position, attributesField, attributeValues, groupById, parentLabel)
}

function processPositionsInPlace(
  positions: Positions,
  attributes: Record<string, TransformedAttributeRecord>,
  groupById: string,
  keySuffix: string,
): void {
  forEach(positions, (position, positionKey) => {
    if (groupById && position.children) {
      forEach(position.children, (child, childKey) => {
        mapAttributesToChildren(child, childKey, attributes, keySuffix, groupById, position.label)
      })
    } else {
      mapAttributesToChildren(position, positionKey, attributes, keySuffix)
    }
  })
}

export function mergeAttributesIntoChildren(
  positions: Positions,
  portfolio: Portfolio,
  groupById: string,
  keySuffix: string,
): Positions {
  const dates = portfolio.dates
  const hasAttributes = dates && !isEmpty(dates)

  if (!hasAttributes) {
    return positions
  }

  const attributes = extractAttributes(dates[0])

  return produce(positions, function mergeAttributesDraft(draftPositions: Positions) {
    processPositionsInPlace(draftPositions, attributes, groupById, keySuffix)
  })
}
